import { Reveal, MaskLineInView } from "../Reveal";
import { useLanguage } from "../../i18n/LanguageContext";
import { Code2, Clapperboard, Camera } from "lucide-react";

const icons = [Code2, Clapperboard, Camera];

/* A genuine hand-of-cards fan in real color, not three white cards with a
   tilt — the center card sits highest and straightest, the outer two rotate
   outward and drop slightly, like a deck spread on a table. Each card gets
   its own block of the brand palette instead of white, so the fan reads as
   confidently as the reference decks it's borrowing the move from. Rotation,
   offset and stacking order are Tailwind classes gated to md — on a phone
   the fan would just read as misaligned, so it collapses to a plain stack —
   and everything here is transform/opacity, so it stays off the main thread. */
/* -ml-14 (56px) looked right in a still shot but at rest it covered the icon
   and the first letters of "Brand Photography" on the card behind — real
   content, not the decorative corner the fan is meant to eat into. Cut to
   -ml-3 (12px): enough to read as a stacked deck, not enough to cross into
   the ~40px inner padding where each card's title actually starts. */
const card = [
  {
    tilt: "md:-rotate-12", shift: "md:translate-y-5", z: "md:z-10", overlap: "",
    bg: "bg-forest", text: "text-cream", sub: "text-cream/70", icon: "text-gold", ghost: "text-cream/10 group-hover:text-gold/25",
  },
  {
    tilt: "md:rotate-0", shift: "md:-translate-y-4", z: "md:z-30", overlap: "md:-ml-3",
    bg: "bg-gold", text: "text-charcoal", sub: "text-charcoal/70", icon: "text-forest", ghost: "text-charcoal/10 group-hover:text-forest/20",
  },
  {
    tilt: "md:rotate-12", shift: "md:translate-y-5", z: "md:z-20", overlap: "md:-ml-3",
    bg: "bg-charcoal", text: "text-cream", sub: "text-cream/65", icon: "text-gold", ghost: "text-cream/10 group-hover:text-gold/25",
  },
];

export const Services = () => {
  const { t } = useLanguage();
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-36 bg-cream overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex items-center gap-3 mb-10">
          <span className="dim-line text-forest" />
          <span className="micro text-forest/70">{t.services.label}</span>
        </div>
        <h2 className="font-display text-5xl md:text-7xl tracking-tight text-charcoal max-w-3xl">
          <MaskLineInView>{t.services.heading[0]}</MaskLineInView>
          <MaskLineInView delay={0.1} className="text-forest">{t.services.heading[1]}</MaskLineInView>
        </h2>
      </div>

      <div className="staff-rule my-12" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-0 md:py-10">
          {t.services.items.map((s, i) => {
            const Icon = icons[i];
            const c = card[i];
            return (
              <Reveal key={i} delay={i * 0.12} className={`md:flex-1 ${c.overlap} ${c.z}`}>
                <div
                  data-testid={`service-card-${i}`}
                  className={`group relative h-full overflow-hidden rounded-3xl border border-black/5 ${c.bg} p-8 md:p-10 shadow-[0_30px_70px_-30px_rgba(26,26,26,0.5)] transition-all duration-500 ease-out md:hover:!translate-y-0 md:hover:!rotate-0 md:hover:z-40 md:hover:scale-105 md:hover:shadow-[0_40px_90px_-25px_rgba(26,26,26,0.55)] ${c.tilt} ${c.shift}`}
                >
                  {/* Oversized index, set behind the copy — the card reads as a
                      numbered plate before you've read a word of it. */}
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute -right-3 -bottom-9 font-display text-[12rem] leading-none transition-colors duration-500 ${c.ghost}`}
                  >
                    {i + 1}
                  </span>

                  <div className="relative">
                    <Icon className={`h-8 w-8 mb-8 ${c.icon}`} strokeWidth={1.4} />
                    <h3 className={`font-display text-2xl md:text-3xl mb-4 leading-tight ${c.text}`}>{s.title}</h3>
                    <p className={`leading-relaxed ${c.sub}`}>{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
